import { useEffect, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, Shield, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface TradesmanProfileIncompleteDialogProps {
  tradesmanProfile: any;
}

export const TradesmanProfileIncompleteDialog = ({ tradesmanProfile }: TradesmanProfileIncompleteDialogProps) => {
  const [open, setOpen] = useState(false);
  const [hasInsurance, setHasInsurance] = useState(false);
  const [hasPortfolio, setHasPortfolio] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkProfileCompleteness = async () => {
      if (!tradesmanProfile?.id) return;

      // Check insurance certificate
      const { data: insurance } = await supabase
        .from("insurance_certificates")
        .select("id, status")
        .eq("tradesman_id", tradesmanProfile.id)
        .in("status", ["pending", "approved"])
        .maybeSingle();

      // Check portfolio photos
      const { data: portfolio } = await supabase
        .from("portfolio_photos")
        .select("id")
        .eq("tradesman_id", tradesmanProfile.id)
        .limit(1)
        .maybeSingle();

      const hasInsuranceDoc = !!insurance;
      const hasPortfolioPhoto = !!portfolio;

      setHasInsurance(hasInsuranceDoc);
      setHasPortfolio(hasPortfolioPhoto);

      // Show dialog if either is missing
      if (!hasInsuranceDoc || !hasPortfolioPhoto) {
        setOpen(true);
      }
    };

    checkProfileCompleteness();
  }, [tradesmanProfile]);

  const handleGoToProfile = () => {
    setOpen(false);
    navigate("/tradesman-profile-setup");
  };

  const handleDismiss = () => {
    setOpen(false);
  };

  // Don't show anything if profile is complete
  if (hasInsurance && hasPortfolio) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-2xl border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-2xl text-red-900">
              Profile Incomplete - Not Receiving Leads
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base text-red-800 space-y-4">
            <div className="bg-white/70 p-4 rounded-lg border border-red-200">
              <p className="font-semibold text-red-900 mb-3">
                ⚠️ Your profile is currently INACTIVE and you will NOT receive any job leads until you complete the following:
              </p>
              
              <div className="space-y-3">
                {!hasInsurance && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <Shield className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-900">Public Liability Insurance Certificate Required</p>
                      <p className="text-sm text-red-700 mt-1">
                        You must upload your valid public liability insurance certificate. This is a legal requirement to work with customers through our platform.
                      </p>
                    </div>
                  </div>
                )}
                
                {!hasPortfolio && (
                  <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <Camera className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-orange-900">Portfolio Photos Required</p>
                      <p className="text-sm text-orange-700 mt-1">
                        Upload at least one photo showcasing your work. This helps customers see the quality of your craftsmanship.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> Even after uploading your insurance certificate, your profile will remain inactive until an admin reviews and approves it. This typically takes 24-48 hours.
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogAction
            onClick={handleDismiss}
            className="bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            Dismiss
          </AlertDialogAction>
          <AlertDialogAction
            onClick={handleGoToProfile}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-700 hover:to-orange-700 shadow-lg"
          >
            Complete Profile Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
